import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronRight } from 'lucide-react';
import { onboardingAPI } from '../lib/api';

/**
 * Toont wat er nog van de eigenaar wordt verwacht voordat de webshop live kan.
 *
 * Elke stap wordt server-side afgeleid uit de onderliggende data, niet uit een
 * opgeslagen vinkje. Een vinkje dat je zelf zet, loopt vroeg of laat uit de pas
 * met wat er werkelijk staat.
 */
export const OnboardingChecklist = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let actief = true;
    (async () => {
      try {
        const { data } = await onboardingAPI.getStatus();
        if (actief) setStatus(data);
      } catch (e) {
        if (actief) setStatus(null);
      }
    })();
    return () => { actief = false; };
  }, []);

  if (!status) return null;

  const open = status.steps.filter(s => !s.done);
  const verplichtOpen = open.filter(s => s.required);

  // Weg zodra het verplichte deel af is. De optionele stappen -- bonprinter,
  // logo, eigen domein -- horen niet eeuwig te blijven staan als aanmaning:
  // wie geen bonprinter wil, is niet onaf. Hij keek eerder naar alle stappen
  // samen, waardoor de kaart bij zo'n restaurant nooit verdween.
  if (verplichtOpen.length === 0) return null;

  // De balk toont de voortgang van het verplichte deel, want dat is wat er nog
  // moet gebeuren voordat de webshop online kan.
  const verplicht = status.steps.filter(s => s.required);
  const klaar = verplicht.length - verplichtOpen.length;
  const pct = Math.round((klaar / Math.max(verplicht.length, 1)) * 100);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-heading font-semibold text-gray-900">
            Nog even dit afronden
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Nog {verplichtOpen.length} {verplichtOpen.length === 1 ? 'stap' : 'stappen'}
            {' '}voordat je webshop online kan.
          </p>
        </div>
        <span className="text-sm font-medium text-gray-500 flex-shrink-0">{klaar}/{verplicht.length}</span>
      </div>

      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4">
        <div className="h-full bg-[#1A4D2E] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>

      {/* Optionele stappen die al af zijn voegen niets toe; wat nog open staat
          blijft wel zichtbaar als suggestie. */}
      <ul className="space-y-1">
        {status.steps.filter(s => s.required || !s.done).map(s => {
          const inhoud = (
            <>
              {s.done
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                : <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />}
              <span className={`flex-1 text-sm ${s.done ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                {s.label}
                {!s.required && <span className="text-gray-400 text-xs"> · optioneel</span>}
                {s.who === 'admin' && <span className="text-gray-400 text-xs"> · doen wij</span>}
              </span>
              {!s.done && s.path && <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
            </>
          );
          return (
            <li key={s.id}>
              {!s.done && s.path ? (
                <Link to={s.path}
                      data-testid={`onboarding-${s.id}`}
                      className="flex items-center gap-2.5 py-1.5 px-2 -mx-2 rounded-lg hover:bg-gray-50">
                  {inhoud}
                </Link>
              ) : (
                <div className="flex items-center gap-2.5 py-1.5 px-2 -mx-2">{inhoud}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
